import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

/**
 * Records audio in-browser via MediaRecorder, sends to /api/needs/transcribe,
 * and passes the transcript to onTranscript(text).
 */
export default function VoiceButton({ onTranscript, className = "" }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (busy) return;
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error("Voice input isn't supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        setRecording(false);
        try {
          streamRef.current?.getTracks().forEach((t) => t.stop());
        } catch {}
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const { data } = await api.post("/needs/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.transcript?.trim()) {
            onTranscript?.(data.transcript.trim());
            toast.success("Transcribed");
          } else {
            toast.error("Nothing heard — try again");
          }
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcription failed");
        }
        setBusy(false);
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch {}
  };

  return (
    <button
      type="button"
      data-testid="voice-button"
      onClick={recording ? stop : start}
      disabled={busy}
      className={`inline-flex items-center gap-2 rounded-full h-10 px-4 text-sm font-medium transition ${recording ? "bg-[#C05A3D] text-white animate-pulse" : "border border-[#C05A3D]/40 text-[#C05A3D] hover:bg-[#C05A3D]/5"} ${className}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (recording ? <Square className="w-4 h-4 fill-current" /> : <Mic className="w-4 h-4" />)}
      {busy ? "Transcribing…" : (recording ? "Stop & transcribe" : "Speak instead")}
    </button>
  );
}
